import React, { Key, useEffect, useMemo, useRef, useState } from "react";

import { InternalOnlyChip } from "manager/components/vero1/InternalOnlyChip";
import { categoryAnchorId } from "manager/components/vero1/openDealReview";
import { rereviewMessage } from "manager/components/vero1/Vero1RereviewBand";
import { Vero1ReviewTrigger } from "manager/components/vero1/Vero1ReviewPanel";
import { ChecklistRow } from "manager/components/vero1/Vero1ReviewPanel/ChecklistRow";
import {
  checkStateLabel,
  clientNameFor,
  formatCategory,
  formatItemValue,
  isAnnotationOnlyItem,
  severityLabel,
} from "manager/components/vero1/Vero1ReviewPanel/formatItemValue";
import {
  isAssessable,
  isAssessed,
  isLeasingTouched,
} from "manager/components/vero1/Vero1ReviewPanel/itemState";
import { orderRowsInGroup } from "manager/components/vero1/Vero1ReviewPanel/orderRows";
import { useVero1ChecklistVersion } from "manager/hooks/api/vero1Queries";
import type {
  Vero1Checklist,
  Vero1ChecklistItem,
  Vero1ChecklistVersionSummary,
  Vero1RereviewChangedRow,
  Vero1RereviewNotice,
} from "shared/interfaces/api/vero1";
import {
  RECOMMENDATION_LABELS,
  resolveVero1RecommendationName,
  Vero1RowKind,
} from "shared/interfaces/api/vero1";

import { formatRunStamp } from "../vero1Dates";

import { readIgnoredRun, storeIgnoredRun } from "./ignoreStore";
import { SEVERITY_CHIP_TONE, groupItems, rowKey } from "./LeasingReviewSection";
import {
  BadgeRow,
  ChipRow,
  FaintNote,
  FilterChip,
  FilteredEmptyLine,
  GhostButton,
  GoldDot,
  GroupHeading,
  GroupHeadingSub,
  HeaderActions,
  HeaderMeta,
  HeaderPad,
  HeaderRow,
  HeaderSelect,
  HoldDot,
  HoldStrip,
  HoldText,
  InTabStrip,
  MetaLine,
  NewResultBadge,
  RunCardActions,
  RunCardBody,
  RunCardHead,
  RunCardHint,
  RunCardLabel,
  RunCardNested,
  RunCardText,
  SectionBody,
  SectionCard,
  SectionTitle,
  SkeletonBar,
  StripText,
  TitleBlock,
  TitleLine,
  VerdictChip,
  VerdictRule,
} from "./styled";

/* Spec 4.1 — the internal VERO1 review section: version selector over every
   submitted version, submitted meta, "N of M items assessed", verdict chip
   with its 2px rule, the gold inline re-run strip, filter chips, grouped
   read-only rows, and per-item "New Lumen result" badges with a nested run
   card and Ignore. */

type Filter = "all" | "changed" | "actions";

/* The open draft is a picker entry of its own. It reads from the GET payload,
   never the version endpoint — a draft is not a submitted version. */
const DRAFT = "draft" as const;
type Selection = number | typeof DRAFT;

/* The picker's status suffix. SUBMITTED is the live version — the spec's
   header reads "Submitted Jul 10 ▾ latest of 2 submissions", so name it
   "current" rather than repeating the date's own meaning. SUPERSEDED is the
   ordinary fate of every prior submission and carries no information the
   ordering doesn't already give, so it is suppressed. NEEDS_REREVIEW and
   INVALIDATED are exceptional and keep their labels. */
const versionStatusSuffix = (status: string, draftOpen: boolean): string => {
  if (draftOpen && (status === "SUBMITTED" || status === "NEEDS_REREVIEW")) {
    return " \u00b7 under revision";
  }
  if (status === "SUBMITTED") return " \u00b7 current";
  if (status === "SUPERSEDED") return "";
  return ` \u00b7 ${status.toLowerCase().split("_").join(" ")}`;
};

const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: "All items" },
  { id: "changed", label: "Changed by VERO1" },
  { id: "actions", label: "Actions for leasing" },
];

const matchesFilter = (item: Vero1ChecklistItem, filter: Filter) =>
  filter === "all" ||
  (filter === "changed"
    ? isLeasingTouched(item)
    : Boolean(item.actionForLeasing));

const changedRowFor = (
  rows: Vero1RereviewChangedRow[] | null | undefined,
  item: Vero1ChecklistItem
) =>
  rows?.find(
    (row) =>
      row.criterionKey === item.criterionKey &&
      String(row.applicationId ?? "") === String(item.application ?? "")
  ) ?? null;

const NewLumenResult: React.FC<{
  dealId: Key;
  item: Vero1ChecklistItem;
  row: Vero1RereviewChangedRow;
  rereview: Vero1RereviewNotice;
}> = ({ dealId, item, row, rereview }) => {
  const [ignoredRun, setIgnoredRun] = useState<string | null>(() =>
    readIgnoredRun(dealId, item.criterionKey, item.application)
  );
  const [open, setOpen] = useState(false);
  if (ignoredRun === rereview.runId) return null;
  const sentilink =
    item.rowKind === Vero1RowKind.Sentilink || isAnnotationOnlyItem(item);
  const freshWord = sentilink
    ? checkStateLabel(row.freshValue) ?? severityLabel(row.freshSeverity)
    : row.freshSeverity != null
    ? severityLabel(row.freshSeverity)
    : null;
  const freshName = sentilink ? null : clientNameFor(row.freshSeverity, item);
  const fresh =
    // the client's name is the result; the severity word only stands in when
    // nothing is configured for it (AC 4)
    freshName ??
    freshWord ??
    (row.freshValue != null
      ? formatItemValue(row.freshValue, row.criterionKey)
      : "no longer assessed");
  const ranAt = rereview.runAt ? formatRunStamp(rereview.runAt) : null;
  return (
    <>
      <BadgeRow>
        <NewResultBadge
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          data-testid="new-lumen-result-badge"
        >
          New Lumen result: {fresh}
        </NewResultBadge>
      </BadgeRow>
      {open && (
        <RunCardNested data-testid="new-lumen-run-card">
          <RunCardHead>
            <RunCardLabel>Lumen{ranAt ? ` · ${ranAt}` : ""}</RunCardLabel>
            <VerdictChip
              $tone={
                row.freshSeverity
                  ? SEVERITY_CHIP_TONE[row.freshSeverity]
                  : undefined
              }
              style={{ height: 19, fontSize: 10.5 }}
            >
              {fresh}
            </VerdictChip>
            <InternalOnlyChip $tone="dark" style={{ marginLeft: "auto" }}>
              Internal only
            </InternalOnlyChip>
          </RunCardHead>
          <RunCardBody>
            <RunCardText>
              {row.label ?? item.label}: {fresh}.
            </RunCardText>
            <RunCardActions>
              <GhostButton
                type="button"
                style={{ height: 28, fontSize: 11.5 }}
                onClick={() => {
                  storeIgnoredRun(
                    dealId,
                    item.criterionKey,
                    item.application,
                    rereview.runId
                  );
                  setIgnoredRun(rereview.runId);
                }}
              >
                Ignore this finding
              </GhostButton>
              <RunCardHint>
                Clears the badge on this row. The run stays in Lumen&apos;s
                history either way.
              </RunCardHint>
            </RunCardActions>
          </RunCardBody>
        </RunCardNested>
      )}
    </>
  );
};

export const Vero1ReviewSection: React.FC<{
  dealId: Key;
  versions: Vero1ChecklistVersionSummary[];
  rereview?: Vero1RereviewNotice | null;
  dealLabel?: string;
  vero1Active?: boolean;
  draftOpen?: boolean;
  draft?: Vero1Checklist | null;
  canAuthor?: boolean;
}> = ({
  dealId,
  versions,
  rereview,
  dealLabel,
  vero1Active,
  draftOpen = false,
  draft = null,
  canAuthor = false,
}) => {
  const newest = versions[0] ?? null;
  const [selectedVersion, setSelectedVersion] = useState<Selection | null>(
    null
  );
  // follow-the-newest: a null selection tracks the current version, and an
  // explicit selection OF the newest means the same intent — so when a fresh
  // submit lands a newer version, a selection pinned to the outgoing newest
  // resets rather than silently viewing what just became history. A selection
  // of an OLDER version is deliberate history-reading and stays put.
  const newestVersion = newest?.version ?? null;
  const prevNewestRef = useRef<number | null>(newestVersion);
  useEffect(() => {
    const prevNewest = prevNewestRef.current;
    prevNewestRef.current = newestVersion;
    if (
      newestVersion != null &&
      prevNewest != null &&
      newestVersion !== prevNewest
    ) {
      setSelectedVersion((selected) =>
        selected === prevNewest ? null : selected
      );
    }
  }, [newestVersion]);
  // a submitted draft leaves the picker; its selection falls back to newest
  useEffect(() => {
    if (!draft) {
      setSelectedVersion((selected) => (selected === DRAFT ? null : selected));
    }
  }, [draft]);
  const selection: Selection | null =
    selectedVersion ?? newest?.version ?? (draft ? DRAFT : null);
  const viewingDraft = selection === DRAFT && Boolean(draft);
  const version = typeof selection === "number" ? selection : null;
  const summary = viewingDraft
    ? null
    : versions.find((v) => v.version === version) ?? newest;
  const { versionDetail, versionLoading } = useVero1ChecklistVersion(
    dealId,
    version,
    !viewingDraft
  );
  const [filter, setFilter] = useState<Filter>("all");

  const items = (viewingDraft ? draft?.items : versionDetail?.items) ?? [];
  const assessable = useMemo(() => items.filter(isAssessable), [items]);
  const assessed = useMemo(
    () => assessable.filter(isAssessed).length,
    [assessable]
  );
  const viewingNewest = version != null && version === newest?.version;
  const { categories, standard, custom } = groupItems(
    items.filter((item) => matchesFilter(item, filter))
  );
  const nothingMatches =
    items.length > 0 && standard.length === 0 && custom.length === 0;

  const submitted = summary?.submittedAt
    ? formatRunStamp(summary.submittedAt)
    : null;
  // the client's authored word wins; the enum label only carries reviews with
  // no configured name, so this chip reads as the banner and list do
  const recommendation = summary?.finalRecommendation
    ? resolveVero1RecommendationName(summary) ??
      RECOMMENDATION_LABELS[summary.finalRecommendation] ??
      summary.finalRecommendation
    : null;
  const verdictTone = summary?.effectiveRecommendationColour
    ? SEVERITY_CHIP_TONE[summary.effectiveRecommendationColour]
    : summary?.finalRecommendation ?? undefined;

  const withBadges = (item: Vero1ChecklistItem) => {
    const row = viewingNewest
      ? changedRowFor(rereview?.changedRows, item)
      : null;
    return (
      <React.Fragment key={rowKey(item)}>
        {row && rereview && (
          <NewLumenResult
            dealId={dealId}
            item={item}
            row={row}
            rereview={rereview}
          />
        )}
        <ChecklistRow item={item} readOnly showApplicant />
      </React.Fragment>
    );
  };

  const settling = !viewingDraft && versionLoading && !versionDetail;
  const pickerCount = versions.length + (draft ? 1 : 0);
  const authorName = viewingDraft
    ? draft?.startedByName ?? draft?.lastEditedByName
    : summary?.submittedByName;

  return (
    <SectionCard
      data-testid="deal-review-vero1-section"
      data-settling={settling || undefined}
    >
      <HeaderPad>
        <HeaderRow>
          <TitleBlock>
            <TitleLine>
              <SectionTitle>VERO1 review</SectionTitle>
              {pickerCount > 1 ? (
                <>
                  <HeaderSelect
                    aria-label="Review version"
                    value={String(selection ?? "")}
                    onChange={(event) =>
                      setSelectedVersion(
                        event.target.value === DRAFT
                          ? DRAFT
                          : Number(event.target.value)
                      )
                    }
                  >
                    {draft && <option value={DRAFT}>In progress</option>}
                    {versions.map((v) => (
                      <option key={v.version} value={v.version}>
                        {`v${v.version} · ${
                          v.submittedAt
                            ? new Date(v.submittedAt).toLocaleDateString([], {
                                month: "short",
                                day: "numeric",
                              })
                            : v.status
                        }${versionStatusSuffix(v.status, draftOpen)}`}
                      </option>
                    ))}
                  </HeaderSelect>
                  {viewingNewest && (
                    <FaintNote>
                      latest of {versions.length} submissions
                    </FaintNote>
                  )}
                </>
              ) : viewingDraft ? (
                <HeaderMeta>In progress</HeaderMeta>
              ) : (
                submitted && <HeaderMeta>Submitted {submitted}</HeaderMeta>
              )}
            </TitleLine>
            <MetaLine data-testid="assessed-count">
              {authorName ? `${authorName} · ` : ""}
              {assessed} of {assessable.length} items assessed
            </MetaLine>
          </TitleBlock>
          {recommendation && (
            // AGREE is not a verdict — it adopts Lumen's result, so the chip
            // takes the server-resolved severity, never the raw enum
            <VerdictChip $tone={verdictTone}>{recommendation}</VerdictChip>
          )}
          {canAuthor && (
            <HeaderActions>
              <Vero1ReviewTrigger
                dealId={dealId}
                vero1Active={vero1Active}
                dealLabel={dealLabel}
                reopen
              />
            </HeaderActions>
          )}
        </HeaderRow>
        {/* the chip stays — it is the record — but the rule goes quiet while
            a restart draft is open, so the header cannot read as the live verdict */}
        <VerdictRule $tone={draftOpen ? undefined : verdictTone} />
      </HeaderPad>
      {viewingNewest && draftOpen && (
        <HoldStrip data-testid="deal-review-draft-strip">
          <HoldDot aria-hidden />
          <HoldText>
            This review is under revision. A new VERO1 review is in progress;
            the findings below stay on record until it&apos;s submitted.
          </HoldText>
        </HoldStrip>
      )}
      {viewingNewest && rereview && (
        <InTabStrip data-testid="deal-review-rereview-strip">
          <GoldDot aria-hidden />
          <StripText>{rereviewMessage(rereview)}.</StripText>
          <InternalOnlyChip $tone="dark">Internal only</InternalOnlyChip>
        </InTabStrip>
      )}
      <ChipRow role="group" aria-label="Filter items">
        {FILTERS.map(({ id, label }) => (
          <FilterChip
            key={id}
            type="button"
            $active={filter === id}
            onClick={() => setFilter(id)}
          >
            {label}
          </FilterChip>
        ))}
      </ChipRow>
      <SectionBody>
        {settling &&
          [0, 1, 2, 3, 4, 5, 6, 7].map((i) => <SkeletonBar key={i} />)}
        {categories.map((category, index) => (
          <React.Fragment key={category}>
            <GroupHeading id={categoryAnchorId(category)} $first={index === 0}>
              {formatCategory(category)}
            </GroupHeading>
            {orderRowsInGroup(
              standard.filter((i) => i.category === category),
              { by: "effective" }
            ).map(withBadges)}
          </React.Fragment>
        ))}
        {custom.length > 0 && (
          <>
            <GroupHeading $first={categories.length === 0}>
              Custom items <GroupHeadingSub>— added by VERO1</GroupHeadingSub>
            </GroupHeading>
            {custom.map(withBadges)}
          </>
        )}
        {nothingMatches && (
          <FilteredEmptyLine>
            No items match this filter. Every result and finding is Lumen&apos;s
            as returned.
          </FilteredEmptyLine>
        )}
      </SectionBody>
    </SectionCard>
  );
};
